import React, { useMemo } from 'react';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import useMeasure from 'react-use-measure';
import AttackBoxToTargetMachineSvg from 'src/images/svgs/attack-box-to-target-machine.svg';
import { RoomTask } from 'src/common/interfaces/tasks';
import { useAppSelector } from 'src/app/hooks';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { StyledBody, StyledImage, StyledWrapper, StyledSubtitle, StyledTitle, StyledVirtualMachinesWrapper, StyledCircleCheckIcon, StyledStaticToast, StyledWarningIcon } from './virtual-environment-setup.styles';
import { VirtualEnvironmentAttackBox } from './virtual-environment-attack-box';
import { VirtualEnvironmentTargetMachine } from './virtual-environment-target-machine';
type VirtualEnvironmentSetupProps = {
  roomCode: string;
  hasSeenVmStart: boolean;
  task: Omit<RoomTask, 'created' | 'deadline' | 'questions'>;
  hasMultipleVMs: boolean;
  isAttackboxWidgetV1ExperimentEnabled: boolean;
  isAttackboxWidgetV2ExperimentEnabled: boolean;
};
export const VirtualEnvironmentSetup: React.FC<VirtualEnvironmentSetupProps> = ({
  roomCode,
  hasSeenVmStart,
  task,
  hasMultipleVMs,
  isAttackboxWidgetV1ExperimentEnabled,
  isAttackboxWidgetV2ExperimentEnabled
}) => {
  const [ref, {
    width
  }] = useMeasure();
  const isWide = useMemo(() => width > BREAKPOINTS.MOBILE, [width]);
  const isAttackboxConnected = useAppSelector(state => !!state.splitScreen.attackBoxPrivateIP);
  const activeMachine = useAppSelector(state => state.room.activeMachine);
  const isAnotherMachineRunning = hasMultipleVMs && !!activeMachine?.uploadId && activeMachine.uploadId !== task.uploadId;
  const isSetupComplete = isAttackboxConnected && !!activeMachine && !isAnotherMachineRunning;
  return <div ref={ref} data-sentry-component="VirtualEnvironmentSetup" data-sentry-source-file="virtual-environment-setup.tsx">
      <StyledWrapper roomCode={roomCode} $isWide={isWide} data-sentry-element="StyledWrapper" data-sentry-source-file="virtual-environment-setup.tsx">
        {isAttackboxWidgetV2ExperimentEnabled ? <span>
            <StyledTitle>
              {isSetupComplete ? 'Virtual environment set up is complete' : 'Set up your virtual environment'}
            </StyledTitle>
            {isSetupComplete && <StyledCircleCheckIcon icon={faCircleCheck} />}
          </span> : <StyledTitle>Set up your virtual environment</StyledTitle>}
        <StyledSubtitle data-sentry-element="StyledSubtitle" data-sentry-source-file="virtual-environment-setup.tsx">
          {isAttackboxWidgetV2ExperimentEnabled && isSetupComplete ? 'All machine details can be found at the top of the page.' : `To successfully complete this room, you'll need to set up your virtual environment. This involves starting
              both your AttackBox and Task Machines, ensuring you're equipped with the necessary tools and access to
              tackle the challenges ahead.`}
        </StyledSubtitle>
        {isAttackboxWidgetV2ExperimentEnabled && isAnotherMachineRunning && <StyledStaticToast>
            <StyledWarningIcon icon={faTriangleExclamation} />
            You already have a machine running. By starting this machine, the previous machine will be terminated.{' '}
            <br />
            Please ensure you have completed your task on the previous machine before proceeding.
          </StyledStaticToast>}
        <StyledBody data-sentry-element="StyledBody" data-sentry-source-file="virtual-environment-setup.tsx">
          <StyledImage $isWide={isWide} src={AttackBoxToTargetMachineSvg} onClick={e => {
          e.stopPropagation();
        }} data-sentry-element="StyledImage" data-sentry-source-file="virtual-environment-setup.tsx" />
          <StyledVirtualMachinesWrapper data-sentry-element="StyledVirtualMachinesWrapper" data-sentry-source-file="virtual-environment-setup.tsx">
            <VirtualEnvironmentAttackBox roomCode={roomCode} isConnected={isAttackboxConnected} data-sentry-element="VirtualEnvironmentAttackBox" data-sentry-source-file="virtual-environment-setup.tsx" />
            <VirtualEnvironmentTargetMachine roomCode={roomCode} hasSeenVmStart={hasSeenVmStart} task={task} activeMachine={activeMachine} hasMultipleVMs={hasMultipleVMs} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} isAnotherMachineRunning={isAnotherMachineRunning} data-sentry-element="VirtualEnvironmentTargetMachine" data-sentry-source-file="virtual-environment-setup.tsx" />
          </StyledVirtualMachinesWrapper>
        </StyledBody>
      </StyledWrapper>
    </div>;
};